import { Button, Spinner, Stack, Text } from "@chakra-ui/react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAcceptContributingEntityInviteMutation } from "../../../autogen/bff-api";
import { useLogout } from "../../../common/auth/useLogout";
import { useApiError } from "../../../common/errors/useApiError";
import { useContributionUrls } from "../../sourcing-events/portal/contributing/useContributionUrls";

interface Props {
  inviteId: string;
  secret: string;
}

export const AcceptContributingEntityInvite = ({ inviteId, secret }: Props) => {
  const contributionUrls = useContributionUrls();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useLogout();
  const displayer = useApiError();
  const [acceptInvite] = useAcceptContributingEntityInviteMutation();
  const [failure, setFailure] = useState<FetchBaseQueryError | SerializedError | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    void (async () => {
      try {
        const detail = await acceptInvite({ inviteId, secret }).unwrap();
        navigate(contributionUrls.view(detail.entity.id), { replace: true });
      } catch (e) {
        setFailure(e as FetchBaseQueryError | SerializedError);
      }
    })();
  }, [acceptInvite, inviteId, secret, navigate]);

  if (failure) {
    return (
      <Stack spacing="4">
        {displayer.show(failure)}
        <Text fontSize="sm">
          {t("If the invitation was sent to someone else, sign in as that person and open the emailed link again.")}
        </Text>
        <Button colorScheme="teal" onClick={() => void logout()}>
          {t("Sign in as another user")}
        </Button>
        <Button variant="link" onClick={() => navigate(contributionUrls.list)}>
          {t("My contributions")}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing="4" align="center">
      <Spinner color="smPrimary" />
      <Text fontSize="sm">{t("Opening the invitation")}...</Text>
    </Stack>
  );
};
